import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    Checkbox,
    Chip,
    Box,
    Typography,
    CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { getFirestore } from 'firebase/firestore';
import { checkMELCompleteness } from '../utils/instructorUtils';

const ExportSummaryModal = ({ isOpen, onClose, roster, studentStatuses, onExport, currentModel, userEmailToId }) => {
    const [selectedStudents, setSelectedStudents] = useState({});
    const [studentMELStatus, setStudentMELStatus] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const hasCheckedRef = useRef(false);
    const isInitializedRef = useRef(false);

    // Reset refs when the modal closes
    useEffect(() => {
        if (!isOpen) {
            hasCheckedRef.current = false;
            isInitializedRef.current = false;
        }
    }, [isOpen]);

    // Initialize data when modal opens
    useEffect(() => {
        if (!isOpen || !roster || !roster.students) return;
        
        // Only initialize once per modal open
        if (isInitializedRef.current) return;
        isInitializedRef.current = true;
        
        // Initialize selected state for all students with MELs
        const initialSelected = {};
        const initialStatus = {};
        
        roster.students.forEach(email => {
            const hasModel = studentStatuses[email.toLowerCase()]?.hasModel;
            initialSelected[email] = hasModel;
            
            if (!hasModel) {
                initialStatus[email] = "No MEL";
            } else {
                // We'll determine if it's partial or complete in a separate function
                initialStatus[email] = "Loading...";
            }
        });
        
        setSelectedStudents(initialSelected);
        setStudentMELStatus(initialStatus);
        
        // Check MEL completeness for students with models
        if (userEmailToId && 
            Object.keys(userEmailToId).length > 0 && 
            roster.students.some(email => studentStatuses[email.toLowerCase()]?.hasModel)) {
            
            // Use setTimeout to ensure state updates have completed
            setTimeout(() => {
                if (!hasCheckedRef.current) {
                    checkAllStudentMELs();
                    hasCheckedRef.current = true;
                }
            }, 0);
        }
    }, [isOpen, roster, studentStatuses]);

    // Handle userEmailToId changes separately
    useEffect(() => {
        if (!isOpen || !roster || !roster.students || hasCheckedRef.current) return;
        
        if (userEmailToId && 
            Object.keys(userEmailToId).length > 0 && 
            roster.students.some(email => studentStatuses[email.toLowerCase()]?.hasModel)) {
            
            checkAllStudentMELs();
            hasCheckedRef.current = true;
        }
    }, [userEmailToId]);

    // Handle currentModel changes separately
    useEffect(() => {
        if (!isOpen || hasCheckedRef.current) return;
        
        if (roster && roster.students && 
            userEmailToId && Object.keys(userEmailToId).length > 0 && 
            roster.students.some(email => studentStatuses[email.toLowerCase()]?.hasModel)) {
            
            checkAllStudentMELs();
            hasCheckedRef.current = true;
        }
    }, [currentModel]);

    const checkAllStudentMELs = async () => {
        if (!roster || !roster.students || !currentModel || !userEmailToId || Object.keys(userEmailToId).length === 0) return;
        
        setIsLoading(true);
        
        const db = getFirestore();
        const newStatus = { ...studentMELStatus }; // Start with current status
        
        for (const email of roster.students) {
            const lowercaseEmail = email.toLowerCase();
            const hasModel = studentStatuses[lowercaseEmail]?.hasModel;
            
            if (hasModel) {
                const studentId = userEmailToId[lowercaseEmail];
                if (studentId) {
                    try {
                        const status = await checkMELCompleteness(db, studentId, currentModel);
                        newStatus[email] = status;
                    } catch (error) {
                        console.error(`Error checking MEL for ${email}:`, error);
                        newStatus[email] = "Error";
                    }
                } else {
                    console.warn(`No user ID found for ${email}`);
                    newStatus[email] = "Error";
                }
            } else {
                newStatus[email] = "No MEL";
            }
        }
        
        setStudentMELStatus(newStatus);
        setIsLoading(false);
    };

    const getStatusChip = (status) => {
        switch(status) {
            case "No MEL":
                return <Chip size="small" label={status} color="error" />;
            case "Partial MEL":
                return <Chip size="small" label={status} color="warning" />;
            case "Completed MEL":
                return <Chip size="small" label={status} color="success" />;
            case "Loading...":
                return <Chip 
                    size="small" 
                    label="Loading..." 
                    icon={<CircularProgress size={12} />} 
                    color="default"
                />;
            default:
                return <Chip size="small" label={status || "Unknown"} color="default" />;
        }
    };

    const handleToggleAll = (checked) => {
        const newSelected = {};
        roster.students.forEach(email => {
            if (studentStatuses[email.toLowerCase()]?.hasModel) {
                newSelected[email] = checked;
            }
        });
        setSelectedStudents(newSelected);
    };

    const handleToggleStudent = (email) => {
        setSelectedStudents(prev => ({
            ...prev,
            [email]: !prev[email]
        }));
    };

    const handleExport = () => {
        const selectedEmails = Object.entries(selectedStudents)
            .filter(([_, isSelected]) => isSelected)
            .map(([email]) => email);
        
        onExport(roster, selectedEmails);
        onClose();
    };

    const selectedCount = Object.values(selectedStudents).filter(Boolean).length;
    const hasModelCount = Object.values(studentStatuses).filter(status => status?.hasModel).length;

    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Export Summary for {roster?.name}</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        Select students to include in the export. Only students with MELs can be exported.
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Button 
                            size="small" 
                            onClick={() => handleToggleAll(true)}
                            disabled={hasModelCount === 0}
                        >
                            Select All
                        </Button>
                        <Button 
                            size="small" 
                            onClick={() => handleToggleAll(false)}
                            disabled={hasModelCount === 0}
                        >
                            Deselect All
                        </Button>
                        <Typography variant="body2">
                            {selectedCount} of {hasModelCount} students selected
                        </Typography>
                    </Box>
                </Box>
                
                <List dense sx={{ maxHeight: '400px', overflow: 'auto' }}>
                    {roster?.students?.map((email) => (
                        <ListItem key={email} divider>
                            <Checkbox
                                edge="start"
                                checked={!!selectedStudents[email]}
                                onChange={() => handleToggleStudent(email)}
                                disabled={!studentStatuses[email.toLowerCase()]?.hasModel}
                            />
                            <ListItemText 
                                primary={email} 
                                secondary={
                                    <Typography component="span" variant="body2">
                                        {getStatusChip(studentMELStatus[email])}
                                    </Typography>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button 
                    onClick={handleExport}
                    startIcon={<CloudUploadIcon />}
                    variant="contained"
                    disabled={selectedCount === 0}
                >
                    Generate Export
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ExportSummaryModal;